import { IContentExtract } from "@/core/shared/api/types";
import { filterStateSchema, IGetFiltersByGroupResult } from "@/core/utils/legend-state/feature/filter";
import { z } from "zod";
import { IPeersetCompany } from "@/features/report-builder/state/types";

const config = z.object({
  company: z.object({
    measure_filter_to_measure_name_map: z.record(
      z.object({
        default: z.string(),
      }),
    ),
    filters: z.object({
      hide: z.boolean(),
    }),
  }),
  measure: z.object({
    hide: z.boolean(),
    default: z.string(),
    measures: z.array(
      z.object({
        id: z.string(),
        label: z.string(),
        unit: z.string().optional(),
        acronym: z.string().optional(),
        showSign: z.boolean().optional(),
        domain: z.tuple([z.number(), z.number()]).optional(),
        compactYAxisValues: z.boolean().optional(),
        filter: z.object({
          checked: z.boolean(),
          disabled: z.boolean().optional(),
        }),
      }),
    ),
  }),
  visualisation: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
    }),
  ),
});

const axis_schema = z.object({
  data_key: z.string().optional(),
  label: z.string().optional(),
  compactValues: z.boolean().optional(),
  domain: z.tuple([z.number(), z.number()]).optional(),
  unit: z.string().optional(),
});

export const local_schema = z.object({
  label: z.string(),
  ui_merged: z.boolean(),
  config,
  views: z.object({
    currentView: z.union([z.literal("analysis"), z.literal("alerts")]),
    analysis: z.object({
      chart_config: z.object({
        entities: z.record(
          z.string(),
          z.object({
            id: z.string(),
            label: z.string(),
            color: z.string(),
          }),
        ),
        polar_axis: axis_schema,
      }),
      selectors: z.object({
        visualisation: z.object({
          selected: z.string(),
        }),
      }),
      filters: z.object({
        company: filterStateSchema,
        measures: filterStateSchema,
        applied_filters: z.object({
          company_id: z.array(z.string()).optional(),
          measures: z.array(z.string()).optional(),
        }),
      }),
    }),
  }),
});

export type ILocalState$ = z.infer<typeof local_schema>;

export type IRemoteState$ = {
  measures: {
    pending: boolean;
    error: boolean;
    data: {
      chart_data: Record<string, number | string | null>[];
      table_data: Record<
        string,
        {
          id: string;
          score: number | string | null;
          label: string;
          signal?: string;
        }
      >[];
    };
  };
};

export interface ICreateStateProps {
  local?: object;
}

export interface IActions {
  views: {
    analysis: {
      selectors: {
        visualisation: {
          set: (instance_id: string, visualisation: string) => void;
        };
      };
      filters: {
        company: {
          applyAll: (instance_id: string) => void;
          toggle: (instance_id: string, filter_id: string) => void;
          toggleGroup: (instance_id: string, filter_group: string) => void;
          getFiltersByGroup: (instance_id: string) => IGetFiltersByGroupResult;
        };
        measures: {
          applyAll: (instance_id: string) => void;
          toggle: (instance_id: string, filter_id: string) => void;
          toggleGroup: (instance_id: string, filter_group: string) => void;
          getFiltersByGroup: (instance_id: string) => IGetFiltersByGroupResult;
        };
      };
    };
  };
}

export interface IObservers {
  view: {
    analysis: {
      chart_config: {
        sync_to_state: (instance_id: string) => void;
      };
      filters: {
        company: {
          init: (instance_id: string) => void;
        };
        measures: {
          init: (instance_id: string) => void;
        };
      };
    };
  };
}

export interface ITransformedContentExtract extends IContentExtract {
  company: IPeersetCompany | null;
}
